import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Smartphone, Download, X, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { INSTALL_UI as UI } from "@/data/installI18n";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { useLatestApk } from "@/hooks/useLatestApk";
import { installedApkCode } from "@/lib/apkVersion";
import { fill } from "@/lib/utils";
import { toast } from "sonner";

const KEY = "install_card_dismissed";

// Dashboard card: one-tap install (APK on Android, PWA prompt elsewhere) + link to the full guide. Hidden once installed/dismissed.
export function InstallHomeCard() {
  const { tc } = useLang();
  const { canInstall, install, installed, busy, device } = usePwaInstall();
  const { latest } = useLatestApk();
  const [hidden, setHidden] = useState(() => localStorage.getItem(KEY) === "1");

  if (hidden || installed || installedApkCode()) return null;

  const dismiss = () => { localStorage.setItem(KEY, "1"); setHidden(true); };
  const onPwa = async () => {
    const r = await install();
    if (r === "accepted") toast.success(tc(UI.installSuccess));
    else if (r === "dismissed") toast(tc(UI.installDismissed));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="relative rounded-3xl border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="install-home-card" data-device={device} data-can-install={canInstall}>
        <button onClick={dismiss} aria-label={tc(UI.dismiss)} data-testid="install-home-dismiss"
          className="absolute top-3 end-3 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-background/80 hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]"><Smartphone className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1 pe-8 sm:pe-0">
          <p className="font-medium">{tc(UI.homeTitle)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{tc(UI.homeHint)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:pe-8">
          {device === "android" ? (
            <a href={latest.path} download data-testid="install-home-apk">
              <Button className="h-10 rounded-full px-5"><Download className="me-2 h-4 w-4" />{fill(tc(UI.downloadApk), { mb: latest.sizeMb })}</Button>
            </a>
          ) : canInstall ? (
            <Button onClick={onPwa} disabled={busy} data-testid="install-home-pwa" className="h-10 rounded-full px-5"><Download className="me-2 h-4 w-4" />{tc(UI.homeCta)}</Button>
          ) : null}
          <Link to="/install" data-testid="install-home-guide" className="inline-flex items-center gap-1.5 h-10 rounded-full border border-border/60 bg-background/70 px-4 text-sm font-medium hover:bg-background">
            {tc(UI.homeGuide)}<ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        </div>
      </Card>
    </motion.div>
  );
}
